/**
 * useUrlState — bidirectional sync between a piece of component state and
 * a single URL search-param key. Shareable URLs, working back button,
 * and no page reloads.
 *
 * Usage:
 *   const [tab, setTab] = useUrlState("tab", "overview");
 *   const [symbol, setSymbol] = useUrlState("symbol", "SCOM");
 *
 * Semantics:
 *   • Reads the initial value from `?<key>=…` on mount; falls back to
 *     `defaultValue` when the param is absent (and NEVER writes the
 *     default back to the URL — keeps URLs clean).
 *   • Writes with `replace: true` (no browser-history spam for tab
 *     toggles). Clicking the browser Back button still works because
 *     the URL reflects each state at time of navigation between pages.
 *   • Accepts an optional `serialize` / `deserialize` pair for non-string
 *     state (numbers, enums). Both default to identity.
 *
 * Does NOT try to be redux — one param, one state slot. Keeps the mental
 * model simple and the hook composable.
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const identity = (v) => v;

export default function useUrlState(
    key,
    defaultValue = "",
    { serialize = identity, deserialize = identity } = {},
) {
    const [params, setParams] = useSearchParams();

    const raw = params.get(key);
    const value = useMemo(
        () => (raw == null ? defaultValue : deserialize(raw)),
        // deserialize is expected to be referentially stable (defined at
        // module scope in the caller); not tracked to avoid infinite loops.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [raw, defaultValue],
    );

    const setValue = useCallback(
        (next) => {
            setParams(
                (prev) => {
                    // Read prev from the LIVE URLSearchParams passed by the
                    // router — not the closure-captured `raw` — so rapid
                    // successive setValue calls compose correctly.
                    const p = new URLSearchParams(prev);
                    const prevSerialized = p.get(key);
                    const prevValue = prevSerialized == null ? defaultValue : deserialize(prevSerialized);
                    const resolved = typeof next === "function" ? next(prevValue) : next;
                    const serialized = serialize(resolved);
                    // Omit the param entirely when it equals the default —
                    // keeps shared URLs short and canonical.
                    if (serialized == null || serialized === "" || serialized === serialize(defaultValue)) {
                        p.delete(key);
                    } else {
                        p.set(key, String(serialized));
                    }
                    return p;
                },
                { replace: true },
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [key, defaultValue, setParams],
    );

    return [value, setValue];
}
